import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CalendarBlank, MapPin, Buildings, CurrencyDollar, Receipt, PaperPlaneTilt, DownloadSimple, X } from "@phosphor-icons/react";
import { api, API } from "@/lib/api";
import { useSettings } from "@/lib/settings";
import { readUtms, trackConversion } from "@/lib/track";

export default function FranchiseOpportunityDetail() {
  const { slug } = useParams();
  const settings = useSettings();
  const [opp, setOpp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [showBrochure, setShowBrochure] = useState(false);

  useEffect(() => {
    setLoading(true); setNotFound(false);
    api.get(`/franchise/opportunities/${slug}`)
      .then((r) => setOpp(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="py-20 text-center text-gos-muted">Loading…</div>;
  if (notFound || !opp) {
    return (
      <div className="py-32 text-center" data-testid="opp-not-found">
        <h1 className="font-display text-3xl text-gos-charcoal">Opportunity not found</h1>
        <Link to="/franchise/opportunities" className="btn-secondary mt-6 inline-flex">
          <ArrowLeft size={14} /> Browse all opportunities
        </Link>
      </div>
    );
  }

  const calendarUrl = settings?.interview_calendar_url;
  const hasMap = !!opp.territory_map_mime;
  const hasBrochure = !!opp.brochure_url;

  return (
    <div data-testid="opp-detail" className="fade-in">
      <section className="py-16 border-b border-gos-border" style={{ background: "var(--gos-bone)" }}>
        <div className="container-gos">
          <Link to="/franchise/opportunities" data-testid="opp-back" className="text-xs uppercase tracking-widest text-gos-slate hover:text-gos-navy inline-flex items-center gap-1 mb-6">
            <ArrowLeft size={14} /> All opportunities
          </Link>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-7">
              <div className="overline mb-2">{opp.industry || "Franchise"}</div>
              <h1 className="font-display text-4xl sm:text-5xl text-gos-charcoal">{opp.name}</h1>
              {opp.tagline && <p className="text-lg text-gos-slate mt-3 italic">{opp.tagline}</p>}
              {opp.description && <p className="text-gos-slate mt-5 leading-relaxed whitespace-pre-wrap">{opp.description}</p>}
              <div className="mt-7 flex flex-wrap gap-3">
                <Link to={`/franchise?opp=${encodeURIComponent(opp.slug)}`} data-testid="opp-apply-btn" className="btn-primary">
                  <PaperPlaneTilt size={14} weight="bold" /> Apply for this opportunity
                </Link>
                {calendarUrl && (
                  <a href={calendarUrl} target="_blank" rel="noreferrer" data-testid="opp-schedule-call" className="btn-secondary">
                    <CalendarBlank size={14} weight="bold" /> Schedule a discovery call
                  </a>
                )}
                {hasBrochure && (
                  <button onClick={() => setShowBrochure(true)} data-testid="opp-brochure-btn" className="btn-secondary">
                    <DownloadSimple size={14} weight="bold" /> Download brochure
                  </button>
                )}
              </div>
            </div>
            <aside className="lg:col-span-5">
              <div className="bg-white border border-gos-border rounded-sm p-6 space-y-4">
                <Detail icon={CurrencyDollar} label="Investment range" value={(opp.investment_min || opp.investment_max) > 0
                  ? `${opp.currency || "USD"} ${Number(opp.investment_min).toLocaleString()} – ${Number(opp.investment_max).toLocaleString()}`
                  : "Contact for details"} />
                <Detail icon={Receipt} label="Franchise fee" value={opp.franchise_fee > 0 ? `${opp.currency || "USD"} ${Number(opp.franchise_fee).toLocaleString()}` : "—"} />
                {opp.royalty_pct > 0 && <Detail icon={Receipt} label="Royalty" value={`${opp.royalty_pct}%`} />}
                {opp.units_operating > 0 && <Detail icon={Buildings} label="Units operating" value={opp.units_operating} />}
                {opp.year_founded > 0 && <Detail icon={CalendarBlank} label="Founded" value={opp.year_founded} />}
                <Detail icon={MapPin} label="Available countries" value={opp.countries?.join(", ") || "Multiple"} />
              </div>
            </aside>
          </div>
        </div>
      </section>

      {hasMap && (
        <section className="py-16" data-testid="opp-territory-section">
          <div className="container-gos">
            <div className="overline mb-2">Territory Map</div>
            <h2 className="font-display text-3xl text-gos-charcoal">Where opportunities are open</h2>
            <p className="text-gos-slate text-sm mt-2 max-w-2xl">
              Shaded regions indicate available territories. Contact us to confirm exclusivity in your target market.
            </p>
            <div className="mt-6 bg-white border border-gos-border rounded-sm p-3">
              <img
                src={`${API}/franchise/opportunities/${opp.slug}/territory-map`}
                alt={`${opp.name} territory map`}
                className="w-full rounded-sm"
                data-testid="opp-territory-map-img"
              />
            </div>
          </div>
        </section>
      )}

      <section className="py-12 border-t border-gos-border" style={{ background: "var(--gos-bone)" }}>
        <div className="container-gos text-center">
          <h3 className="font-display text-2xl text-gos-charcoal">Ready to take the next step?</h3>
          <p className="text-gos-slate mt-2">Submit an application — our team will respond within 3 business days.</p>
          <Link to={`/franchise?opp=${encodeURIComponent(opp.slug)}`} className="btn-primary mt-5">
            Apply now <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
      </section>

      {showBrochure && <BrochureModal opp={opp} onClose={() => setShowBrochure(false)} />}
    </div>
  );
}

function BrochureModal({ opp, onClose }) {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", company: "", country: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setSending(true);
    try {
      const r = await api.post(`/franchise/opportunities/${opp.slug}/request-brochure`, {
        ...form,
        utm: readUtms() || {},
      });
      trackConversion("brochure_request", { value: 15 });
      setSent(true);
      toast.success(r.data.message || "Brochure sent. Check your inbox.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send the brochure");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="brochure-modal">
      <div className="bg-white max-w-md w-full rounded-sm border border-gos-border" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-5 border-b border-gos-border">
          <div>
            <div className="overline">Request brochure</div>
            <h3 className="font-display text-xl text-gos-charcoal mt-1">{opp.name}</h3>
          </div>
          <button onClick={onClose} data-testid="brochure-close" className="text-gos-slate hover:text-gos-navy"><X size={18} /></button>
        </div>

        {sent ? (
          <div className="p-6 text-center" data-testid="brochure-sent">
            <DownloadSimple size={36} weight="duotone" className="text-gos-navy mx-auto" />
            <h4 className="font-display text-xl text-gos-charcoal mt-3">Check your inbox</h4>
            <p className="text-sm text-gos-slate mt-2">We&apos;ve emailed the brochure to <b>{form.email}</b>. Look for it from {(opp.name || "Open Business Anywhere")}.</p>
            <button onClick={onClose} className="btn-secondary mt-5 inline-flex">Close</button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 space-y-3" data-testid="brochure-form">
            <p className="text-xs text-gos-slate">Enter your details and we&apos;ll email the brochure for you to download.</p>
            <BField label="Full name *" value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} testid="brochure-name" />
            <BField label="Email *" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="brochure-email" />
            <BField label="Phone (optional)" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="brochure-phone" />
            <div className="grid grid-cols-2 gap-3">
              <BField label="Company" value={form.company} onChange={(v) => setForm({ ...form, company: v })} testid="brochure-company" />
              <BField label="Country" value={form.country} onChange={(v) => setForm({ ...form, country: v })} testid="brochure-country" />
            </div>
            <button type="submit" disabled={sending} data-testid="brochure-submit" className="btn-primary w-full mt-2">
              {sending ? "Sending…" : "Send me the brochure"}
            </button>
            <p className="text-[10px] text-gos-muted text-center">By submitting you agree to be contacted by our team about this opportunity.</p>
          </form>
        )}
      </div>
    </div>
  );
}

function BField({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.18em] text-gos-muted mb-1">{label}</label>
      <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full border border-gos-border rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gos-navy" />
    </div>
  );
}

function Detail({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon size={20} weight="duotone" className="text-gos-navy shrink-0 mt-0.5" />
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-[0.18em] text-gos-muted">{label}</div>
        <div className="text-sm text-gos-charcoal">{value}</div>
      </div>
    </div>
  );
}
